import { Eveflow } from 'src/apis/eveflow';
import { GaPaymentButtonType, Gtm } from 'src/apis/gtm';
import { Klaviyo } from 'src/apis/klaviyo';
import { config } from 'src/config';
import { CardService } from 'src/services/CardService';
import { getPlaceComponentTypeName } from 'src/utils/autocomplete';
import { getCookie, setCookieWithExpiry } from 'src/utils/cookies';
import { debounce } from 'src/utils/debounce';
import { getVisibleElementById } from 'src/utils/dom';
import { getAppUtms } from 'src/utils/utms';
import { validateEmail, validateMvpLead } from 'src/utils/validations';

import { FunnelController } from '../business/FunnelController';
import { CheckoutController } from '../pages/CheckoutController';
import { UiController } from '../ui/UiController';

export interface IBuyButtonIds {
  applePayButtonId: string;
  payPalButtonId: string;
  googlePayButtonId: string;
  cardButtonId: string;
  klarnaButtonId: string;
  klarnaStripeButtonId: string;
}

export class CheckoutFormController {
  private fc: FunnelController;
  private checkout: CheckoutController;
  private validateFields = false;
  private buyButtonIds: IBuyButtonIds;
  private cardService = new CardService();

  constructor(
    fc: FunnelController,
    checkout: CheckoutController,
    buyButtonIds: IBuyButtonIds,
    validateFields?: boolean,
  ) {
    this.fc = fc;
    this.checkout = checkout;
    this.buyButtonIds = buyButtonIds;
    this.validateFields = validateFields || false;
    this.fc.lead.prefillForm();
  }

  private reportToAnalytics = () => {
    const lead = this.fc.lead.lead;
    Gtm.trackEmail(lead.emailAddress);
    Klaviyo.trackActiveOnSite(lead);
  };

  private initKlavyioEmailEvent = (): void => {
    const emailField = document.getElementById(
      'emailAddress',
    ) as HTMLInputElement | null;

    const handleBlur = (): void => {
      if (emailField && validateEmail(emailField.value)) {
        const email = emailField.value;
        const firstName = this.fc.lead?.lead?.firstName || '';
        const lastName = this.fc.lead?.lead?.lastName || '';
        const locale = this.fc.config?.locale || '';
        Klaviyo.trackActiveOnSite({
          emailAddress: email,
          firstName,
          lastName,
        });
        const product = this.fc.getSelectedProduct();

        if (sessionStorage.getItem('ev_click_id')) {
          Eveflow.trackAddToCart(
            window.BRAND,
            sessionStorage.getItem('ev_click_id').split(',')[0],
          );
        }

        Klaviyo.trackStartedCheckoutV2(
          email,
          firstName,
          lastName,
          product,
          locale,
        );
      }
    };

    if (emailField) {
      emailField.addEventListener('blur', handleBlur);
    }
  };

  private initImportLeadEvents = (): void => {
    const fields: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      '#postalCode, #phoneNumber, #lastName',
    );

    const debouncedEventHandler = debounce(async () => {
      if (validateMvpLead()) {
        await this.fc.lead.createLead();
        this.reportToAnalytics();
      }
    }, 300);

    fields.forEach((field: HTMLInputElement) => {
      field.addEventListener('blur', debouncedEventHandler);
    });
  };

  private initCardInputFormatters = () => {
    this.cardService.initCardNumberFormatter(
      'cardNumber',
      'cardCvv',
      this.validateFields,
    );
    this.cardService.initExpiryFormatter('cardExpiry', this.validateFields);
    this.cardService.initCvvFormatter('cardCvv', this.validateFields);
  };

  private initPhoneInput = async (componentId = 'phoneNumber') => {
    const numberInput: any = getVisibleElementById<'input'>(componentId);
    // const showFlags = numberInput?.attributes?.showflags?.value || true;
    const intlTelInput = (await import(
      'intl-tel-input'
    )) as unknown as IntelTel;
    const intlTelUtils =
      this.fc.config.klarnaEnabled || this.fc.config.stripeKlarnaEnabled
        ? undefined
        : await import('intl-tel-input/build/js/utils');

    if (numberInput) {
      const iti = intlTelInput(numberInput, {
        separateDialCode: !this.fc.config.klarnaEnabled,
        utilsScript: intlTelUtils,
        initialCountry: this.fc.lead.lead?.country || window.GEO.country,
        allowDropdown: false,
        excludeCountries: ['RU', 'AF', 'KP', 'IR', 'SO', 'XK'],
      });

      const countryDropdown = document.querySelector(
        '.iti__flag-container',
      ) as HTMLElement;
      // if (
      //   (countryDropdown && this.fc.config.klarnaEnabled) ||
      //   (countryDropdown && this.fc.config.stripeKlarnaEnabled) ||
      //   (countryDropdown && showFlags == 'false')
      // ) {
      //   countryDropdown.style.display = 'none';
      //   numberInput.style.paddingLeft = '12px';
      // }

      if (countryDropdown) {
        countryDropdown.style.display = 'none';
      }

      numberInput.addEventListener('input', () => {
        iti.setNumber(numberInput.value);
      });
    }
  };

  private initCountrySelector = async () => {
    const countryInput = getVisibleElementById<'select'>('country');
    if (countryInput) {
      const countries = UiController.getCountriesArray();
      UiController.populateDropdown(
        countryInput,
        countries,
        'name',
        'code',
        this.fc.lead.lead?.country || window.GEO.country,
      );

      const stateInput = getVisibleElementById<'select'>('state');

      countryInput.addEventListener('change', async () => {
        const states = await UiController.getCountryStates(countryInput.value);
        // Reinitialize Klarna after country change
        this.checkout.maybeInitKlarna(countryInput.value);

        if (stateInput) {
          UiController.removeNonDefaultOptions(stateInput);
          UiController.populateDropdown(stateInput, states, 'name', 'code');
          stateInput.value = '';
        }
      });

      if (stateInput) {
        const initialStates = await UiController.getCountryStates(
          countryInput.value,
        );
        UiController.populateDropdown(
          stateInput,
          initialStates,
          'name',
          'code',
          this.fc.lead.lead?.state,
        );
      }
    }
  };

  private setLoading = (
    pressedButtonId: string,
    loading: boolean,
    loadFor?: number,
  ) => {
    UiController.showModalLoader(loading);
    UiController.setButtonLoading(pressedButtonId, loading);
    const otherButtons = Object.values(this.buyButtonIds);
    otherButtons.forEach(btnId => {
      UiController.setButtonDisabled(btnId, loading);
    });

    if (loadFor) {
      setTimeout(() => this.setLoading(pressedButtonId, false), loadFor);
    }
  };

  private paymentPrerequisites = async (
    paymentType: GaPaymentButtonType,
    validate = false,
    mid?: string | number,
  ) => {
    const auid = getAppUtms()?.custom_order_auid;
    const sessionId =
      new URLSearchParams(window.location.search).get('session_id') || '';

    Gtm.trackBuyButtonClick(
      this.fc.lead.lead?.customerId?.toString() || '',
      this.fc.lead.lead?.orderId || '',
      paymentType,
      auid,
      sessionId,
      mid,
    );

    const lead = await this.fc.lead.createLead(paymentType, validate);
    if (lead !== null) {
      const product = this.fc.getSelectedProduct();
      Klaviyo.trackAddToCart(lead, product);

      Klaviyo.trackStartedCheckoutV2(
        lead.emailAddress,
        lead.firstName,
        lead.lastName,
        product,
        this.fc.config.locale || 'en',
      );
    }

    return lead;
  };

  private initCreditCard = () => {
    this.initCardInputFormatters();
    const cardButtonId = this.buyButtonIds.cardButtonId;
    const cardButton = document.getElementById(cardButtonId);
    if (!cardButton) {
      throw new Error('Credit card button undefined');
    }
    cardButton.addEventListener('click', async ev => {
      ev.preventDefault();
      this.setLoading(cardButtonId, true);
      const lead = await this.paymentPrerequisites('credit_card', true);
      if (!lead) {
        this.setLoading(cardButtonId, false);
        return;
      }
      await this.checkout.createCreditCardOrder(this.cardService.cardFormData);
      setTimeout(() => {
        this.setLoading(cardButtonId, false);
      }, 3000);
    });
  };

  private initPayPal = () => {
    const ppButtonId = this.buyButtonIds.payPalButtonId;
    const ppButton = document.getElementById(ppButtonId);
    if (!ppButton) {
      throw new Error('PayPal button undefined');
    }
    ppButton.addEventListener('click', async ev => {
      ev.preventDefault();
      this.setLoading(ppButtonId, true);
      const { paypalMid, paypalType } = this.checkout.getParams();
      const paymentType =
        paypalType === 'default' ? `paypal` : `paypal_${paypalType}`;

      if (!this.fc.config.expressPaypal) {
        const lead = await this.paymentPrerequisites(
          paymentType,
          true,
          paypalMid,
        );

        if (!lead) {
          this.setLoading(ppButtonId, false);
          return;
        }
      } else {
        const auid = getAppUtms().custom_order_auid;
        const sessionId =
          new URLSearchParams(window.location.search).get('session_id') ||
          this.fc.lead.lead?.sessionId;

        Gtm.trackBuyButtonClick(
          this.fc.lead.lead?.customerId?.toString() || '',
          this.fc.lead.lead?.orderId || '',
          paymentType,
          auid,
          sessionId,
          paypalMid,
        );

        if (sessionStorage.getItem('ev_click_id')) {
          Eveflow.trackAddToCart(
            window.BRAND,
            sessionStorage.getItem('ev_click_id').split(',')[0],
          );
        }
      }

      await this.checkout.createPayPalOrder();
      if (paypalType === 'reach') {
        setTimeout(() => this.setLoading(ppButtonId, false), 5000);
      } else {
        this.setLoading(ppButtonId, false);
      }
    });
  };

  private maybeInitKlarna = () => {
    const { klarnaEnabled } = this.fc.config;
    if (klarnaEnabled) {
      const klarnaButtonId = this.buyButtonIds.klarnaButtonId;
      const klarnaButton = document.getElementById(klarnaButtonId);
      if (!klarnaButton) {
        throw new Error('Klarna button undefined');
      }
      klarnaButton.addEventListener('click', async ev => {
        ev.preventDefault();
        this.setLoading(klarnaButtonId, true);
        const lead = await this.paymentPrerequisites('klarna', true);
        if (!lead) {
          this.setLoading(klarnaButtonId, false);
          return;
        }
        await this.checkout.createKlarnaOrder();
        setTimeout(() => {
          this.setLoading(klarnaButtonId, false);
        }, 3000);
      });
    }
  };

  private maybieInitStripeKlarna = () => {
    const { stripeKlarnaEnabled } = this.fc.config;
    if (stripeKlarnaEnabled) {
      const stripeKlarnaButtonId = this.buyButtonIds.klarnaStripeButtonId;
      const stripeKlarnaButton = document.getElementById(stripeKlarnaButtonId);

      if (!stripeKlarnaButton) {
        throw new Error('Stripe Klarna button undefined');
      }

      stripeKlarnaButton.addEventListener('click', async ev => {
        ev.preventDefault();
        this.setLoading(stripeKlarnaButtonId, true);

        const lead = await this.paymentPrerequisites('klarna_stripe', true);
        if (!lead) {
          this.setLoading(stripeKlarnaButtonId, false);
          return;
        }
        await this.checkout.createKlarnaStripeOrder();
        this.setLoading(stripeKlarnaButtonId, false);
      });
    }
  };

  private maybeInitExpressKlarna = () => {
    const { klarnaClientId, expressKlarnaEnabled } = this.fc.config;
    if (expressKlarnaEnabled && klarnaClientId) {
      window.Klarna.Payments.Buttons.init({
        client_id: klarnaClientId,
      });
      window.Klarna.Payments.Buttons.load(
        {
          container: `#klarna-express-container`,
          theme: 'default',
          shape: 'rect',
          locale: this.fc.config.locale,
          on_click: (authorize: any) => {
            UiController.setElementLoading(
              'klarna-express-container',
              true,
              'start',
            );
            this.checkout.createExpressKlarnaOrder(authorize, () =>
              UiController.setElementLoading(
                'klarna-express-container',
                false,
                'end',
              ),
            );
          },
        },
        function load_callback(loadResult: any) {
          if (window.DEBUG) {
            console.log('loadResult: ', loadResult);
          }
        },
      );
    }
  };

  private maybeInitGooglePay = () => {
    const { googlePayEnabled } = this.fc.config;
    if (googlePayEnabled) {
      const gpayButtonId = this.buyButtonIds.googlePayButtonId;
      const gpayButton = document.getElementById(gpayButtonId);
      if (!gpayButton) {
        throw new Error('GPay button undefined');
      }
      gpayButton.addEventListener('click', async ev => {
        ev.preventDefault();
        this.setLoading(gpayButtonId, true, 5000);

        const mid = this.fc.config.braintreeAppleGooglePayMerchantId
          ? this.fc.config.braintreeAppleGooglePayMerchantId
          : this.fc.config.appleGooglePayMerchantId;
        const lead = await this.paymentPrerequisites(
          `google_pay_${mid}`,
          true,
          mid,
        );
        if (!lead) {
          this.setLoading(gpayButtonId, false);
          return;
        }
        await this.checkout.createGooglePayOrder();
        this.setLoading(gpayButtonId, false);
      });
    }
  };

  private maybeInitApplePay = () => {
    const { applePayEnabled } = this.fc.config;
    if (applePayEnabled) {
      const appleButtonId = this.buyButtonIds.applePayButtonId;
      const appleButton = document.getElementById(appleButtonId);
      if (!appleButton) {
        throw new Error('Apple button undefined');
      }
      appleButton.addEventListener('click', async ev => {
        ev.preventDefault();
        this.setLoading(appleButtonId, true);

        await this.checkout.initAppleSession(
          this.paymentPrerequisites,
          this.fc.config.expressApplePay,
        );
        this.setLoading(appleButtonId, false);
      });
    }
  };

  private maybieInitSaveParamsToCookie = () => {
    const urlParams = getCookie('urlParams');

    if (!urlParams) {
      const urlQueryParams = new URLSearchParams(window.location.search);
      const paramsObject: { [key: string]: string } = {};

      urlQueryParams.forEach((value, key) => {
        paramsObject[key] = value;
      });

      paramsObject['http_referer'] = window.location.href;
      const serializedParams = JSON.stringify(paramsObject);
      setCookieWithExpiry('urlParams', serializedParams, 15);
    }
  };

  private maybeInitStreetAutocomplete = () => {
    const shouldLoad = this.fc.config.streetAutocomplete;
    if (shouldLoad) {
      const srcUrl = `https://maps.googleapis.com/maps/api/js?key=${config.PLACES_API}&libraries=places`;
      loadScript(srcUrl, true, {
        type: 'text/javascript',
      });
      waitForCondition(
        () =>
          typeof google !== 'undefined' &&
          typeof google?.maps?.places !== 'undefined',
        () => {
          const addressFieldId = this.fc.config.addressFieldId || 'address1';
          const cityFieldId = this.fc.config.cityFieldId || 'city';
          const countryFieldId = this.fc.config.countryFieldId || 'country';
          const stateFieldId = this.fc.config.stateFieldId || 'state';
          const postalCodeFieldId =
            this.fc.config.postalCodeFieldId || 'postalCode';

          const addressInputs = Array.from(
            document.querySelectorAll(`#${addressFieldId}`),
          ) as HTMLInputElement[];
          const cityInputs = Array.from(
            document.querySelectorAll(`#${cityFieldId}`),
          ) as HTMLInputElement[];
          const stateInputs = Array.from(
            document.querySelectorAll(`#${stateFieldId}`),
          ) as HTMLSelectElement[];
          const countryInputs = Array.from(
            document.querySelectorAll(`#${countryFieldId}`),
          ) as HTMLSelectElement[];
          const postalCodeInputs = Array.from(
            document.querySelectorAll(`#${postalCodeFieldId}`),
          ) as HTMLInputElement[];

          addressInputs.forEach(input => {
            const autocomplete = new google.maps.places.Autocomplete(input, {
              fields: ['address_components', 'formatted_address'],
              types: ['street_address', 'street_number', 'premise'],
            });

            if (autocomplete) {
              autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (window.DEBUG) {
                  console.log('place: ', { place });
                }
                const formattedAddress = place?.formatted_address || '';
                const addressFromFormattedAddress = formattedAddress
                  ? formattedAddress.split(',')[0]
                  : '';

                input.value = addressFromFormattedAddress;

                const postalCode: string = getPlaceComponentTypeName(
                  place.address_components,
                  'postal_code',
                  'long_name',
                );
                const postalCodeSuffix: string = getPlaceComponentTypeName(
                  place.address_components,
                  'postal_code_suffix',
                  'long_name',
                );
                postalCodeInputs.forEach(function (postCodeElement) {
                  postCodeElement.value = `${postalCode}${
                    postalCodeSuffix ? '-' + postalCodeSuffix : ''
                  }`;
                });

                const locality: string =
                  getPlaceComponentTypeName(
                    place.address_components,
                    'locality',
                    'long_name',
                  ) ||
                  getPlaceComponentTypeName(
                    place.address_components,
                    'postal_town',
                    'long_name',
                  );
                cityInputs.forEach(function (cityElement) {
                  cityElement.value = locality;
                });

                const country: string = getPlaceComponentTypeName(
                  place.address_components,
                  'country',
                );
                let state: string = getPlaceComponentTypeName(
                  place.address_components,
                  'administrative_area_level_1',
                );
                if (state.length > 3) {
                  state = '';
                }

                UiController.getCountryStates(country).then(states => {
                  stateInputs.forEach((stateInput: HTMLSelectElement) => {
                    UiController.removeNonDefaultOptions(stateInput);
                    UiController.populateDropdown(
                      stateInput,
                      states,
                      'name',
                      'code',
                    );
                    stateInput.value = state;
                  });
                });

                countryInputs.forEach(function (countryElement) {
                  countryElement.value = country;
                });
              });
            }
          });
        },
        () =>
          Errors.report(
            new Error(
              'AutocompleteSrc: Google Places API failed to load in 5s',
            ),
            undefined,
            false,
            false,
          ),
      );
    }
  };

  initCheckoutForm = async () => {
    // Form
    this.initKlavyioEmailEvent();
    this.initImportLeadEvents();
    this.initPhoneInput();
    await this.initCountrySelector();
    this.maybeInitStreetAutocomplete();

    if (window['country'].children.length === 0) {
      this.initCountrySelector();
      Errors.report(
        new Error('AddressFormError: Country selector fallback was used'),
        {},
        false,
        false,
      );
    }

    //Other
    this.maybieInitSaveParamsToCookie();

    // Payments
    this.initPayPal();
    this.initCreditCard();
    this.maybieInitStripeKlarna();
    this.maybeInitKlarna();
    this.maybeInitApplePay();
    this.maybeInitGooglePay();
    this.maybeInitExpressKlarna();
  };
}
